import { LightningElement, api, wire } from "lwc";
import { logger } from "@devforce/trailhead-client-logger";
import { EffectAdapter } from "common/effectAdapter";
import { getAssetsUrl, fetchAsset } from "common/helpers";

const DEFAULT_SPRITE = "utility";
const fetches = new Map();
class icon extends LightningElement {
  @api sprite = DEFAULT_SPRITE;
  @api size = "medium";
  @api symbol = "";

  @wire(EffectAdapter, {
    sprite: "$sprite",
    symbol: "$symbol",
  })
  async onChange() {
    if (this.willFetch) {
      this.appendNodes().catch((e) => logger.warn(e.message));
    }
  }

  get className() {
    return `size--${this.size}`;
  }

  get spriteUrl() {
    // eslint-disable-next-line local/import-image-path
    return getAssetsUrl(`/icons/${this.sprite}-sprite/svg/symbols.svg`);
  }

  get sdsUrl() {
    // eslint-disable-next-line local/import-image-path
    return `https://api.lightningdesignsystem.com/sds/icons/foundation/${this.symbol}.svg`;
  } // Use the SDS CDN if available

  get willUseSDS() {
    return false; // TODO re-enable once salesforce-ux has properly tuned their CDN
    // return this.sprite === "utility";
  }

  get willFetch() {
    return this.willUseSDS || /^http/.test(this.spriteUrl);
  }

  get href() {
    return `${this.spriteUrl}#${this.symbol}`;
  }

  get symbolId() {
    return `tds__${this.sprite}__${this.symbol}`;
  }

  renderedCallback() {
    if (this.willFetch) {
      this.appendNodes().catch((e) => logger.warn(e.message));
    }
  }
  /**
   * Fetch the icon and append svg code to the DOM
   */

  async appendNodes() {
    // Don't re-fetch if the icon is already set
    if (this.node?.id === this.symbolId) return;
    const svg = this.template.querySelector("svg");
    const icon = await this.fetchIcon(); // Warn if icon couldn't be fetched

    if (!icon) {
      logger.error(`Could not load icon symbol "${this.symbol}".`);
      return;
    }

    const node = icon.cloneNode(true); // Remove previous nodes

    while (svg.firstChild) svg.removeChild(svg.firstChild); // Insert new nodes

    for (let n of Array.from(node.childNodes)) svg.appendChild(n);

    svg.setAttribute("viewBox", node.getAttribute("viewBox"));
    this.node = node;
  }

  async fetchIcon() {
    return this.willUseSDS
      ? this.fetchFromSDS().catch(() => {
          // Fallback to sprite
          return this.fetchFromSprite();
        })
      : this.fetchFromSprite();
  }

  async fetchFromSDS() {
    let promise;

    if (fetches.has(this.sdsUrl)) {
      promise = fetches.get(this.sdsUrl);
    } else {
      promise = fetchAsset(this.sdsUrl);
      fetches.set(this.sdsUrl, promise);
    }

    return promise.then(async (code) => {
      const div = document.createElement("div");
      div.innerHTML = code;
      const svg = div.firstElementChild; // Ensure fill color is set properly

      Array.from(svg.querySelectorAll("[fill]"))?.forEach((n) => {
        n.setAttribute("fill", "currentColor");
      });
      return svg;
    });
  }

  async fetchFromSprite() {
    let promise;

    if (fetches.has(this.spriteUrl)) {
      promise = fetches.get(this.spriteUrl);
    } else {
      promise = fetchAsset(this.spriteUrl)
        .then((code) => {
          return code.replace(/id="/g, `id="tds__${this.sprite}__`);
        })
        .then((code) => {
          const div = document.createElement("div");
          div.style.display = "none";
          div.innerHTML = code;
          document.body.appendChild(div);
          return code;
        });
      fetches.set(this.spriteUrl, promise);
    }

    return promise.then(() => {
      return document.querySelector(`#${this.symbolId}`);
    });
  }
}

export default icon;
export { DEFAULT_SPRITE };
